// frontend/src/lib/socket-context.tsx
// Brighella v2 (ADR-014 D10) — shared WebSocket client with per-channel
// subscribe/presence support.
//
// One WebSocket per session, authenticated with the same JWT the REST API
// uses. Channels (`production:42`, `calendar:acme:2026-05`, ...) carry both
// presence updates and entity-level invalidation messages.
//
// Public API
//   const { subscribe, unsubscribe, presence } = useSocket()
//   const members = useChannelPresence('production:42')

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getAccessToken } from '../utils/api.js'

interface WsMessage {
  type?: string
  channel?: string
  queryKey?: unknown
  members?: unknown
  [key: string]: unknown
}

type MessageListener = (msg: WsMessage) => void

export interface SocketContextValue {
  subscribe: (channel: string) => void
  unsubscribe: (channel: string) => void
  presenceMap: Map<string, unknown[]>
  addMessageListener: (fn: MessageListener) => () => void
}

const SocketContext = createContext<SocketContextValue | null>(null)

// Audit phase 1.7 — exponential backoff with cap.
// Previously the reconnect interval was a constant 5 s, which hammers the
// server every 5 s during a long outage. We now back off exponentially from
// the configured base (1 s for the no-token path, 5 s for the post-open
// close path) and cap at MAX_BACKOFF_MS. retryCount is reset to 0 the first
// time the server sends `auth_ok` (a real successful connection), so a
// transient blip in long-running tabs doesn't keep the backoff stuck on a
// long interval.
const MAX_BACKOFF_MS = 60_000  // 60 s ceiling — slow enough to be polite, fast enough to recover
export const _backoffMs = (attempt: number, base: number): number =>
  Math.min(MAX_BACKOFF_MS, Math.round(base * Math.pow(2, Math.max(0, attempt))))

export function SocketProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [retryCount, setRetryCount] = useState(0)
  // Bumped on every presence change. Drives the ctx memo below so presence
  // consumers re-render, WITHOUT making ctx a fresh object every render.
  const [presenceVersion, setPresenceVersion] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const subscribedRef = useRef<Set<string>>(new Set())            // channels we want
  const presenceRef = useRef<Map<string, unknown[]>>(new Map())   // channel → Member[]
  const authedRef = useRef(false)
  // True once we've authenticated at least once. Lets us distinguish the first
  // connect from a RE-connect so we can catch up on invalidations the server
  // pushed while the socket was down (Finding #2, silent-state audit 2026-06-07).
  const hasConnectedRef = useRef(false)
  // Phase 35 — per-message listeners. Hooks (DM, etc.) register a callback
  // here; the provider's onmessage dispatches every incoming WS frame to
  // every listener. Each listener filters by type/channel itself.
  const listenersRef = useRef<Set<MessageListener>>(new Set())

  // Bump a render so consumers re-read presence snapshots.
  const ping = useCallback(() => setPresenceVersion(n => n + 1), [])

  const sendIfOpen = useCallback((payload: unknown) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload))
      return true
    }
    return false
  }, [])

  const subscribe = useCallback((channel: string) => {
    if (!channel) return
    subscribedRef.current.add(channel)
    if (authedRef.current) sendIfOpen({ type: 'subscribe', channel })
  }, [sendIfOpen])

  const unsubscribe = useCallback((channel: string) => {
    if (!channel) return
    subscribedRef.current.delete(channel)
    presenceRef.current.delete(channel)
    sendIfOpen({ type: 'unsubscribe', channel })
    ping()
  }, [sendIfOpen, ping])

  // Phase 35 — listener API. Returns the unsubscribe fn.
  const addMessageListener = useCallback((fn: MessageListener) => {
    if (typeof fn !== 'function') return () => {}
    listenersRef.current.add(fn)
    return () => listenersRef.current.delete(fn)
  }, [])

  useEffect(() => {
    const token = getAccessToken?.()
    if (!token) {
      // Try again later — token may arrive after login.
      // Exponential backoff capped at MAX_BACKOFF_MS so we don't hammer
      // the no-token path during a long unauthenticated session.
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null
        setRetryCount(r => r + 1)
      }, _backoffMs(retryCount, 1000))
      return undefined
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`)
    wsRef.current = ws
    authedRef.current = false

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token }))
    }

    ws.onmessage = (event: MessageEvent) => {
      let msg: WsMessage
      try { msg = JSON.parse(event.data) as WsMessage } catch { return }

      if (msg.type === 'auth_ok') {
        authedRef.current = true
        // Audit phase 1.7 — reset backoff to 0 on a real successful auth,
        // so a transient blip later doesn't keep us stuck at a long interval.
        // We only reset if retryCount is non-zero to avoid the wasted render.
        setRetryCount(prev => (prev === 0 ? prev : 0))
        // Re-subscribe all desired channels after reconnect / first auth.
        for (const channel of subscribedRef.current) {
          ws.send(JSON.stringify({ type: 'subscribe', channel }))
        }
        // Catch up on missed invalidations. Any `invalidate`/`entity_update`
        // pushed while the socket was down never reached us, so on a RE-connect
        // (not the first auth) refetch all active queries. Without this, data
        // mutated elsewhere during the outage stays stale until a manual refresh
        // — the exact "needs-refresh" class this audit targets. (Finding #2.)
        if (hasConnectedRef.current) {
          qc.invalidateQueries()
        }
        hasConnectedRef.current = true
        return
      }

      if (msg.type === 'invalidate' && Array.isArray(msg.queryKey)) {
        qc.invalidateQueries({ queryKey: msg.queryKey })
        return
      }

      if (msg.type === 'entity_update') {
        if (Array.isArray(msg.queryKey)) {
          qc.invalidateQueries({ queryKey: msg.queryKey })
        }
        return
      }

      if (msg.type === 'presence_update' && msg.channel) {
        presenceRef.current.set(msg.channel, Array.isArray(msg.members) ? msg.members : [])
        ping()
        return
      }

      // UX-audit N4 — in-app notification pushed by lib/notify.js. Keep the
      // bell badge + notification list live: useUnreadCount/useNotifications
      // intentionally poll-free (refetchInterval: false), so WS invalidation
      // is their ONLY refresh path. No `return` — fall through so registered
      // listeners (toasts, per-feature hooks) can also react to the frame.
      if (msg.type === 'notification') {
        qc.invalidateQueries({ queryKey: ['unread-count'] })
        qc.invalidateQueries({ queryKey: ['notifications'] })
      }

      if (msg.type === 'subscribe_error') {
        // Drop the channel from our wanted set so we stop retrying.
        if (msg.channel) {
          subscribedRef.current.delete(msg.channel)
          presenceRef.current.delete(msg.channel)
          ping()
        }
      }

      // Phase 35 — fan out every message to all registered listeners.
      // Hooks filter by type/channel themselves. Wrapped in try so a
      // misbehaving listener never breaks the dispatch loop.
      for (const fn of listenersRef.current) {
        try { fn(msg) } catch { /* listener error — swallow */ }
      }
    }

    ws.onclose = () => {
      authedRef.current = false
      // Audit phase 1.7 — reconnect with exponential backoff, capped at
      // MAX_BACKOFF_MS. retryCount is reset to 0 on auth_ok above, so the
      // sequence after a successful connect is 5s → 10s → 20s → 40s → 60s
      // (capped) instead of 5s, 5s, 5s, 5s forever.
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null
        setRetryCount(r => r + 1)
      }, _backoffMs(retryCount, 5000))
    }

    ws.onerror = () => ws.close()

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      ws.onclose = null
      try { ws.close() } catch { /* ignore */ }
      wsRef.current = null
      authedRef.current = false
    }
  }, [qc, retryCount, ping])

  // Context value — recomputed only when presence actually changes
  // (presenceVersion), never on an unrelated render. A fresh object every
  // render would re-render every consumer; combined with useRealtimeEntity's
  // effect (which must NOT depend on this object — it depends on the stable
  // subscribe/unsubscribe fns) that previously span an infinite
  // subscribe/unsubscribe loop that flooded the socket and OOM'd the server.
  const ctx = useMemo<SocketContextValue>(
    () => ({ subscribe, unsubscribe, presenceMap: presenceRef.current, addMessageListener }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscribe, unsubscribe, addMessageListener, presenceVersion],
  )

  return <SocketContext.Provider value={ctx}>{children}</SocketContext.Provider>
}

export const useSocket = (): SocketContextValue | null => useContext(SocketContext)

/** ADR-014 D10 — read presence for a channel. Returns Member[]. */
export function useChannelPresence(channel: string | null | undefined): unknown[] {
  const ctx = useContext(SocketContext)
  if (!ctx || !channel) return []
  return ctx.presenceMap.get(channel) || []
}
